import { useEffect } from 'react'
import { Card } from './ui/card'
import { Pill } from './ui/pill'
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs'
import { useSystemStatus, useDeviceStatus, useStreamingStatus, useSystemActions } from '../stores/systemStore'
import { useSignalQuality, useRealtimeAnalysis } from '../stores/processedDataStore'
import { useUIStore } from '../stores/uiStore'
import { EmptyState } from './ui/EmptyState'
import { PageShell, PageHeader } from './ui/PageShell'
import { Activity } from 'lucide-react'
import EEGVisualizer from './Visualizer/EEG/EEGVisualizer'
import PPGVisualizer from './Visualizer/PPG/PPGVisualizer'
import ACCVisualizer from './Visualizer/ACC/ACCVisualizer'
import { useTranslation } from '../locales'
import { useLanguageStore } from '../stores/languageStore'

export function Visualizer() {
  const { currentLanguage } = useLanguageStore()
  const t = useTranslation(currentLanguage)
  const { systemStatus } = useSystemStatus()
  const { isConnected } = useDeviceStatus()
  const { isStreaming } = useStreamingStatus()
  const { initializeSystem } = useSystemActions()
  const signalQuality = useSignalQuality()
  const realtimeAnalysis = useRealtimeAnalysis()
  const { setActiveMenu } = useUIStore()

  useEffect(() => {
    if (systemStatus === 'idle') {
      initializeSystem()
    }
  }, [systemStatus, initializeSystem])

  // SQI 색상 매핑
  const sqiColorClass =
    signalQuality.overall === 'excellent' ? 'text-green-400' :
    signalQuality.overall === 'good' ? 'text-indigo-400' :
    signalQuality.overall === 'fair' ? 'text-yellow-400' :
    'text-red-400'

  if (!isConnected) {
    return (
      <PageShell>
        <PageHeader title={t.visualizer.title} subtitle={t.visualizer.subtitle} />

        <Card className="p-6">
          <div className="flex flex-col items-center justify-center min-h-96">
            <div className="text-center">
              <h3 className="font-medium mb-2">{t.visualizer.deviceRequired}</h3>
              <p className="t-soft mb-6">
                {t.visualizer.deviceRequiredDesc}
              </p>
              <div className="flex flex-col items-center gap-6">
                <Pill variant="secondary" size="md" disabled>
                  {systemStatus === 'initializing' ? t.visualizer.initializing : t.visualizer.waiting}
                </Pill>
                <Pill variant="primary" size="md" onClick={() => setActiveMenu('linkband')}>
                  {t.visualizer.connectLinkBand}
                </Pill>
              </div>
            </div>
          </div>
        </Card>
      </PageShell>
    )
  }

  return (
    <PageShell>
      {/* Header */}
      <PageHeader title={t.visualizer.title} subtitle={t.visualizer.subtitle}>
        <Pill size="sm" variant="secondary">
          {isStreaming ? t.visualizer.streaming : t.visualizer.waiting}
        </Pill>
        <Pill size="sm" variant="secondary" className={sqiColorClass}>
          SQI: {signalQuality.overall}
        </Pill>
      </PageHeader>

      {/* 실시간 분석 결과 요약 */}
      <div className="hidden">
      {!realtimeAnalysis.heartRate && !realtimeAnalysis.focusLevel && !realtimeAnalysis.stressLevel && (
        <EmptyState
          icon={<Activity className="w-8 h-8 text-muted-foreground" strokeWidth={1.5} />}
          title={t.visualizer.noSensorData}
          description={isStreaming ? t.visualizer.receivingDataDesc : t.visualizer.startStreamingDesc}
        />
      )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {realtimeAnalysis.heartRate && (
          <Card className="p-4">
            <span className="lb-eyebrow">HEART RATE</span>
            <div className="text-2xl font-medium t-strong mt-2">
              {realtimeAnalysis.heartRate.toFixed(0)} BPM
            </div>
          </Card>
        )}

        {realtimeAnalysis.focusLevel && (
          <Card className="p-4">
            <span className="lb-eyebrow">FOCUS LEVEL</span>
            <div className="text-2xl font-medium t-strong mt-2">
              {realtimeAnalysis.focusLevel.toFixed(0)}%
            </div>
          </Card>
        )}

        {realtimeAnalysis.stressLevel && (
          <Card className="p-4">
            <span className="lb-eyebrow">STRESS LEVEL</span>
            <div className="text-2xl font-medium t-strong mt-2">
              {realtimeAnalysis.stressLevel.toFixed(0)}%
            </div>
          </Card>
        )}
      </div>

      {/* 탭 기반 시각화 */}
      <Card className="p-4">
        <Tabs defaultValue="eeg" className="h-full">
          <TabsList className="w-full">
            <TabsTrigger value="eeg" className="flex-1">{t.visualizer.eegBrainwave}</TabsTrigger>
            <TabsTrigger value="ppg" className="flex-1">{t.visualizer.ppgHeartRate}</TabsTrigger>
            <TabsTrigger value="acc" className="flex-1">{t.visualizer.accAcceleration}</TabsTrigger>
          </TabsList>

          <TabsContent value="eeg" className="mt-6 min-h-[800px]">
            <EEGVisualizer />
          </TabsContent>

          <TabsContent value="ppg" className="mt-6 min-h-[800px]">
            <PPGVisualizer />
          </TabsContent>

          <TabsContent value="acc" className="mt-6 min-h-[800px]">
            <ACCVisualizer />
          </TabsContent>
        </Tabs>
      </Card>
    </PageShell>
  )
}
